import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@components/moleculs/autocomplete';
import { useCountryAutocomplete } from '@components/moleculs/autocomplete/hooks/use-country-autocomplete';
import { TextField } from '@components/moleculs/text-field';
import type { LogoSelectorProps } from '@components/organisms/language-selector/types';
import { useCountryOptions } from '@hooks/options';
import { AVAILABLE_LANGUAGES } from '@lang/localization';
import type { LangEnum } from '@models/enums';
import type { AutocompleteOption } from '@models/types';

export const LanguageSelector: FC<LogoSelectorProps> = ({ className, onChange }) => {
    const { t, i18n } = useTranslation();
    const { renderFlagOption, renderInputFlag } = useCountryAutocomplete();
    const countryOptions = useCountryOptions();

    const options = useMemo(
        () =>
            AVAILABLE_LANGUAGES.map((language) => ({
                label: t(`languageSelector.language.${language}`),
                value: language,
            })),
        [t],
    );

    const value = useMemo(
        () => options.find((option) => option.value === i18n.language),
        [i18n.language, options],
    );

    const flag = countryOptions.find((option) => option.value.toLowerCase() === i18n.language);

    const renderFlag = useCallback(
        (...params: Parameters<typeof renderFlagOption>) =>
            renderFlagOption(
                params[0],
                params[1].value === 'en' ? { ...params[1], value: 'gb' } : params[1],
                params[2],
            ),
        [renderFlagOption],
    );

    return (
        <Autocomplete
            value={value}
            fullWidth={true}
            onChange={(e, option) => {
                if (option) {
                    const { value: val } = option as AutocompleteOption<LangEnum>;

                    i18n.changeLanguage((option as AutocompleteOption<string>).value);
                    onChange?.(val);
                }
            }}
            isOptionEqualToValue={(option, val) => option.label === val.label}
            renderInput={({ inputProps, InputProps, ...rest }) => (
                <TextField
                    {...rest}
                    InputProps={{
                        ...InputProps,
                        startAdornment: renderInputFlag(flag?.value || 'GB'),
                        readOnly: true,
                    }}
                    /* eslint-disable-next-line react/jsx-no-duplicate-props */
                    inputProps={{
                        ...inputProps,
                        readOnly: true,
                    }}
                    inputSize='s'
                />
            )}
            options={options}
            disableClearable={true}
            className={className}
            renderOption={renderFlag}
        />
    );
};
